import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { isEmpty } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { combineLatest } from 'rxjs';
import { auditTime, filter, take } from 'rxjs/operators';
import { Camera, Vector2, WebGLRenderer } from 'three';
import { environment } from '../../../environments/environment';
import { getAnimationFrameScheduler } from '../../cross-team-shared/misc-utils';
import { assertDefined } from '../../cross-team-shared/misc-utils/strict-record';
import { Point2D } from '../../team-apis/otterdam/answers/shape-typings';
import { MainCameraService } from '../render/cameras/main-camera.service';
import { OrthographicCameraService } from '../render/cameras/orthographic-camera.service';
import { ProjectionCamerasService } from '../render/cameras/projection-cameras.service';
import { SideViewCamerasService } from '../render/cameras/side-view-cameras.service';
import { MaterialService } from '../render/material.service';
import { AbstractMeshHelper } from '../render/mesh/abstract-mesh-helper';
import { AbstractMeshService } from '../render/mesh/abstract-mesh.service';
import { PointCloudService } from '../render/mesh/cloud/point-cloud.service';
import { CuboidService } from '../render/mesh/cuboid/cuboid.service';
import { TrainingCuboidService } from '../render/mesh/cuboid/training-cuboid.service';
import { FreePointMeasurementService } from '../render/mesh/free-point-measurement/free-point-measurement.service';
import { InProgressCuboidService } from '../render/mesh/in-progess-cuboid/in-progress-cuboid.service';
import { LineService } from '../render/mesh/poly-shape/line/line.service';
import { TrainingLineService } from '../render/mesh/poly-shape/line/training-line.service';
import { PolygonService } from '../render/mesh/poly-shape/polygon/polygon.service';
import { TrainingPolygonService } from '../render/mesh/poly-shape/polygon/training-polygon.service';
import { SensorIndicatorService } from '../render/mesh/sensor-indicator/sensor-indicator.service';
import { SubpointMeasurementService } from '../render/mesh/subpoint-measurement/subpoint-measurement.service';
import { RenderTriggerService } from '../render/render-trigger.service';
import { RendererLayer } from '../render/render-utils';
import { SceneControllerService } from '../render/scene-controller.service';
import { PointCloudContentStore } from '../state/point-cloud-content-store';
import { ViewRegion } from '../state/view-region-state';
import { createRenderer } from '../utils/point-cloud-utils';

@Component({
  selector: 'h3-scene-canvas',
  templateUrl: './scene-canvas.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SceneCanvasComponent
  extends DirectiveSuperclass
  implements AfterViewInit, OnDestroy
{
  @Input({ required: true }) viewRegionNames!: string[];
  @Input({ required: true }) size!: Point2D;
  @Input() useTransparentBackground = true;

  @ViewChild('canvas')
  private canvasRef!: ElementRef<HTMLCanvasElement>;

  canvasElement!: HTMLCanvasElement;

  private renderer!: WebGLRenderer;

  private meshServices: Array<AbstractMeshService<AbstractMeshHelper>> = [
    inject(CuboidService),
    inject(FreePointMeasurementService),
    inject(PointCloudService),
    inject(LineService),
    inject(PolygonService),
    inject(SensorIndicatorService),
    inject(SubpointMeasurementService),
    inject(TrainingCuboidService),
    inject(TrainingLineService),
    inject(TrainingPolygonService),
    inject(InProgressCuboidService),
  ];

  private materialService = inject(MaterialService);
  private renderTriggerService = inject(RenderTriggerService);
  private sceneControllerService = inject(SceneControllerService);
  private mainCameraService = inject(MainCameraService);
  private orthographicCameraService = inject(OrthographicCameraService);
  private projectionCamerasService = inject(ProjectionCamerasService);
  private sideViewCamerasService = inject(SideViewCamerasService);
  private store = inject(PointCloudContentStore);

  ngAfterViewInit() {
    this.canvasElement = this.canvasRef.nativeElement;
    this.setUpSubscriptions();
  }

  ngOnDestroy() {
    if (environment.shouldForceWebglContextLoss) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- may be undefined on init
      this.renderer?.forceContextLoss();
    }
  }

  onWebglContextRestored() {
    const size = this.renderer.getSize(new Vector2()).toArray();
    this.renderer.dispose();
    this.renderer = this.createRenderer(size);
    this.renderAll();
  }

  private setUpSubscriptions() {
    this.subscribeTo(
      combineLatest([
        this.getInput$('viewRegionNames'),
        this.getInput$('size'),
      ]).pipe(
        filter(([regions, size]) => !isEmpty(regions) && !isEmpty(size)),
        take(1),
      ),
      () => {
        this.initializeComposer();
        this.subscribeToSize();
        this.subscribeToRender();
      },
    );
  }

  private subscribeToSize() {
    this.subscribeTo(this.getInput$('size'), (size) => {
      this.renderer.setSize(size[0], size[1]);
      this.renderAll();
    });
  }

  private subscribeToRender() {
    this.subscribeTo(
      combineLatest([
        this.getInput$('viewRegionNames'),
        this.renderTriggerService.trigger$,
      ]).pipe(
        // run render on the next animation frame using only the latest result
        auditTime(0, getAnimationFrameScheduler()),
      ),
      () => this.renderAll(),
    );
  }

  private initializeComposer() {
    this.renderer = this.createRenderer(this.size);
    this.renderAll();
  }

  private renderAll() {
    this.renderer.setScissorTest(false);
    this.renderer.clear();
    this.renderer.setScissorTest(true);

    const viewRegions = this.getViewRegions();

    for (const viewRegion of viewRegions) {
      this.renderer.setViewport(
        viewRegion.lowerLeftCorner[0],
        viewRegion.lowerLeftCorner[1],
        viewRegion.size[0],
        viewRegion.size[1],
      );
      this.renderer.setScissor(
        viewRegion.lowerLeftCorner[0],
        viewRegion.lowerLeftCorner[1],
        viewRegion.size[0],
        viewRegion.size[1],
      );

      const camera = this.getCameraForLayer(
        viewRegion.layer,
        viewRegion.outputName,
      );

      this.prepareToRender(viewRegion, camera);

      this.renderer.render(this.sceneControllerService.getScene(), camera);
    }
  }

  private prepareToRender(viewRegion: ViewRegion, camera: Camera) {
    this.materialService.setCurrentOutputName(viewRegion.outputName);

    this.meshServices.forEach((meshService) =>
      meshService.prepareToRender(viewRegion, camera),
    );
  }

  private createRenderer(size: Point2D) {
    const renderer = createRenderer(this.canvasElement);
    renderer.setClearColor(0x000000, this.useTransparentBackground ? 0 : 1);
    renderer.setSize(size[0], size[1]);
    return renderer;
  }

  // eslint-disable-next-line complexity -- not really that complex
  private getCameraForLayer(rendererLayer: RendererLayer, outputName: string) {
    switch (rendererLayer) {
      case RendererLayer.Main:
        return this.mainCameraService.camera;
      case RendererLayer.Orthographic:
        return this.orthographicCameraService.camera;
      case RendererLayer.SideViewBottom:
      case RendererLayer.SideViewMiddle:
      case RendererLayer.SideViewTop:
        return this.sideViewCamerasService.getCameraByRendererLayer(
          rendererLayer,
        );
      case RendererLayer.Projection:
        return this.projectionCamerasService.getCamera(outputName);
      default:
        throw new Error(`Invalid renderer layer: ${rendererLayer}.`);
    }
  }

  private getViewRegions() {
    const { viewRegions } = this.store.state();
    return this.viewRegionNames.map((viewRegionName) => {
      const viewRegion = viewRegions.regions[viewRegionName];
      assertDefined(viewRegion);
      return viewRegion;
    });
  }
}
